// Booking a job (office-booking prototype, technical design §6.5): the arrival windows offered, what
// still stops the booking, overlaps for the technicians picked, and the jobs.book input. Pure, so
// it is tested without a browser; times are the company's, whatever the browser's zone.

import { ARRIVAL_WINDOWS } from '@flowology/domain';
import { addDays, dateText, daysBetween, hourRangeText, sentences } from '@flowology/kernel';

/** Arrival windows offered, as [start hour, end hour] in the company's time zone: capacity's too (BL-057). */
export const DAY_WINDOWS: readonly (readonly [number, number])[] = ARRIVAL_WINDOWS;
/** After hours: evening and night, for emergencies (audit F-12). */
export const AFTER_HOURS_WINDOWS: readonly (readonly [number, number])[] = [
  [18, 20],
  [20, 22],
  [22, 24],
];

export interface Draft {
  customerId: string | null;
  locationId: string | null;
  businessUnitId: string | null;
  jobTypeId: string | null;
  kind: 'estimate_visit' | 'service';
  summary: string;
  date: string;
  window: readonly [number, number] | null;
  techIds: string[];
  afterHours: boolean;
  afterHoursReason: string;
  callId: string | null;
  /** Booking a lead: jobs.book marks it booked (leads stay open until then). */
  leadId: string | null;
  sendConfirmation: boolean;
  /** The customer's purchase order, when they give one now (F-29); Review asks for it if it's required. */
  poNumber: string;
  /** BL-129: the tags picked, each saved to the job, the customer, or both. */
  tags: BookTag[];
}

/** BL-129: a tag picked when booking, and where it goes: the job (at first), the customer (if ticked). */
export interface BookTag {
  id: string;
  name: string;
  job: boolean;
  customer: boolean;
}

/** A tag offered when booking: the customer's page offers the same (no system or retired tags). */
export interface TagOption {
  id: string;
  name: string;
  system?: boolean;
  active?: boolean;
}

/** What the tag picker offers: the company's tags a person may put on, less those already picked. */
export const tagOptions = (all: readonly TagOption[], picked: readonly BookTag[]) =>
  all.filter((t) => !t.system && t.active !== false && !picked.some((p) => p.id === t.id));

/** Pick a tag: saved to this job, not to the customer, until the boxes say otherwise. */
export const pickTag = (tags: readonly BookTag[], t: { id: string; name: string }): BookTag[] =>
  tags.some((x) => x.id === t.id)
    ? [...tags]
    : [...tags, { id: t.id, name: t.name, job: true, customer: false }];

/** Tick or untick one of a picked tag's boxes. */
export const tagTo = (tags: readonly BookTag[], id: string, to: 'job' | 'customer', on: boolean): BookTag[] =>
  tags.map((t) => (t.id === id ? { ...t, [to]: on } : t));

export const dropTag = (tags: readonly BookTag[], id: string): BookTag[] => tags.filter((t) => t.id !== id);

export function emptyDraft(today: string): Draft {
  return {
    customerId: null,
    locationId: null,
    businessUnitId: null,
    jobTypeId: null,
    kind: 'estimate_visit',
    summary: '',
    date: today,
    window: null,
    techIds: [],
    afterHours: false,
    afterHoursReason: '',
    poNumber: '',
    callId: null,
    leadId: null,
    sendConfirmation: true,
    tags: [],
  };
}

/** What still stops the booking, in the words the button's hint uses. */
export function blockers(d: Draft): string[] {
  return [
    !d.customerId && 'pick a customer',
    d.customerId && !d.locationId && 'pick a location',
    !d.jobTypeId && 'pick a job type',
    !d.summary.trim() && 'add a summary for the tech',
    !d.window && 'pick an arrival window',
    d.afterHours && !d.afterHoursReason.trim() && 'say why it can’t wait',
  ].filter((x): x is string => typeof x === 'string');
}

/** The zone's offset from UTC at instant `t` (ms). */
function offsetMs(t: number, timeZone: string): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(new Date(t));
  const get = (k: string) => Number(parts.find((p) => p.type === k)!.value);
  return (
    Date.UTC(get('year'), get('month') - 1, get('day'), get('hour') % 24, get('minute'), get('second')) - t
  );
}

/** The instant of local wall time `date` `hour`:00 in `timeZone` (hour 24 is the next midnight). */
export function zonedInstant(date: string, hour: number, timeZone: string): string {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  const wall = Date.UTC(y, m - 1, d, hour);
  let t = wall;
  for (let i = 0; i < 2; i++) t = wall - offsetMs(t, timeZone); // twice: settles across a DST change
  return new Date(t).toISOString();
}

/** "8–10 a.m.", "12–2 p.m.", "10 p.m.–12 a.m.". */
export function windowLabel([s, e]: readonly [number, number]): string {
  return hourRangeText(s, e);
}

/** Today plus the next days, labelled the way the office says them. */
export function dayOptions(today: string, days = 7): { date: string; label: string }[] {
  return Array.from({ length: days }, (_, i) => {
    const d = new Date(`${today}T12:00:00Z`);
    d.setUTCDate(d.getUTCDate() + i);
    const date = d.toISOString().slice(0, 10);
    const label =
      i === 0 ? 'Today' : i === 1 ? 'Tomorrow' : dateText(date, 'UTC', { weekday: 'short', year: false });
    return { date, label };
  });
}

/**
 * Whether every daytime window today is over, so today has nothing left to book (after hours aside).
 * The one under way is still booked into (QA-829).
 */
export const todayPassed = (nowHour: number): boolean => DAY_WINDOWS.every((w) => w[1] <= nowHour);

/** The day booking opens on: today, or tomorrow once today's windows are all over. */
export function firstOpenDay(today: string, nowHour: number): string {
  return todayPassed(nowHour) ? dayOptions(today, 2)[1]!.date : today;
}

/**
 * The windows offered for a day. A window that has already started today is shown but disabled;
 * after hours, today also offers one starting now, since an emergency cannot wait for the next slot.
 */
export function windowsFor(
  afterHours: boolean,
  date: string,
  today: string,
  nowHour: number,
): { window: readonly [number, number]; label: string; started: boolean }[] {
  const base = (afterHours ? AFTER_HOURS_WINDOWS : DAY_WINDOWS).map((w) => ({
    window: w,
    label: windowLabel(w),
    started: windowStarted(date, today, w, nowHour),
  }));
  if (!afterHours || date !== today) return base;
  const now = [nowHour, nowHour + 2] as const;
  return [
    { window: now, label: `Now (${windowLabel(now)})`, started: false },
    ...base.filter((x) => !x.started),
  ];
}

/**
 * A window today that is over: Book no longer offers it. The one under way still is, for a visit
 * this morning that isn't an emergency (QA-829).
 */
export function windowOver(
  date: string,
  today: string,
  w: readonly [number, number],
  nowHour: number,
): boolean {
  return date === today && w[1] <= nowHour;
}

/** A window that has already started today is not offered. */
export function windowStarted(
  date: string,
  today: string,
  w: readonly [number, number],
  nowHour: number,
): boolean {
  return date === today && w[0] <= nowHour;
}

export interface BoardTech {
  id: string;
  name: string;
  visits: { appointmentId: string; jobNumber: string; start: string; end: string }[];
}

/** Visits the picked technicians already have that overlap the window ("Sam has #12 8–10 am"). */
export function overlaps(
  techs: readonly BoardTech[],
  techIds: readonly string[],
  window: { start: string; end: string } | null,
): { techName: string; jobNumber: string }[] {
  if (!window) return [];
  return techs
    .filter((t) => techIds.includes(t.id))
    .flatMap((t) =>
      t.visits
        .filter((v) => v.start < window.end && window.start < v.end)
        .map((v) => ({ techName: t.name, jobNumber: v.jobNumber })),
    );
}

/**
 * The job types offered for the department picked: its own, and every one for "Any department", which
 * is offered for every department and for none (QA-721). With no departments, the "Any" ones.
 */
export function typesFor<T extends { businessUnitId: string | null }>(
  types: readonly T[],
  businessUnitId: string | null,
): T[] {
  return types.filter((t) => !t.businessUnitId || t.businessUnitId === businessUnitId);
}

/** A field of the form a refusal can name (QA-720), marked on the form. */
export type BookField =
  | 'customer'
  | 'location'
  | 'department'
  | 'jobType'
  | 'summary'
  | 'kind'
  | 'window'
  | 'techs'
  | 'afterHours'
  | 'po'
  | 'tags';

/** jobs.book's (and estimates.bookSold's) input fields, as the form shows them. */
const BOOK_FIELDS: Record<string, BookField> = {
  customerId: 'customer',
  locationId: 'location',
  businessUnitId: 'department',
  jobTypeId: 'jobType',
  summary: 'summary',
  kind: 'kind',
  poNumber: 'po',
  tagIds: 'tags',
  customerTagIds: 'tags',
};
const IN_APPOINTMENT: Record<string, BookField> = {
  techIds: 'techs',
  afterHours: 'afterHours',
  afterHoursReason: 'afterHours',
};
/** The words "Check the …" uses for each. */
const FIELD_WORDS: Record<BookField, string> = {
  customer: 'customer',
  location: 'location',
  department: 'department',
  jobType: 'job type',
  summary: 'summary',
  kind: 'visit kind',
  window: 'arrival window',
  techs: 'technicians',
  afterHours: 'reason it can’t wait',
  po: 'PO number',
  tags: 'tags',
};

/**
 * What a refused booking says, and the field to mark (QA-720): the server's message, naming the field
 * its first issue is at ("… not valid. Check the job type."). Null when the refusal names none.
 */
export function bookRefusal(e: { message: string; detail?: unknown }): {
  message: string;
  field: BookField | null;
} {
  const issues = (e.detail as { issues?: { path?: (string | number)[] }[] } | null)?.issues ?? [];
  const path = issues.find((i) => i.path?.length)?.path ?? [];
  const field =
    path[0] === 'appointment'
      ? (IN_APPOINTMENT[String(path[1])] ?? 'window')
      : (BOOK_FIELDS[String(path[0])] ?? null);
  const said = field && !e.message.includes(`Check the ${FIELD_WORDS[field]}`);
  return { message: said ? `${e.message} Check the ${FIELD_WORDS[field]}.` : e.message, field };
}

/** Whether the form holds anything typed or picked: leaving it then keeps it (QA-723). */
export function draftHasInput(d: Draft, query: string): boolean {
  return !!(
    d.customerId ||
    query.trim() ||
    d.jobTypeId ||
    d.summary.trim() ||
    d.window ||
    d.techIds.length ||
    d.afterHoursReason.trim() ||
    d.poNumber.trim() ||
    d.tags.length
  );
}

/**
 * The jobs.book input for a complete draft. `note`: the dispatcher's note (booked against capacity),
 * saved on the job with it (QA-696). `checkDuplicate`: refused, naming it, when the customer already
 * has a visit for the same job type near it (QA-698); "Book anyway" sends it without.
 */
export function bookInput(
  d: Draft,
  timeZone: string,
  o: { note?: string | null; checkDuplicate?: boolean } = {},
) {
  const [s, e] = d.window!;
  const start = zonedInstant(d.date, s, timeZone);
  const end = zonedInstant(d.date, e, timeZone);
  return {
    customerId: d.customerId!,
    locationId: d.locationId!,
    // none picked (a company with no departments): the job type's, or none for "Any" (QA-720)
    ...(d.businessUnitId ? { businessUnitId: d.businessUnitId } : {}),
    jobTypeId: d.jobTypeId!,
    kind: d.kind,
    summary: d.summary.trim(),
    appointment: {
      start,
      end,
      arrivalWindow: { start, end },
      techIds: d.techIds,
      ...(d.afterHours ? { afterHours: true, afterHoursReason: d.afterHoursReason.trim() } : {}),
    },
    ...(d.callId ? { sourceCallId: d.callId } : {}),
    ...(d.leadId ? { leadId: d.leadId } : {}),
    ...(d.poNumber.trim() ? { poNumber: d.poNumber.trim() } : {}),
    // BL-129: a tag with neither box ticked goes nowhere
    ...(d.tags.some((t) => t.job) ? { tagIds: d.tags.filter((t) => t.job).map((t) => t.id) } : {}),
    ...(d.tags.some((t) => t.customer)
      ? { customerTagIds: d.tags.filter((t) => t.customer).map((t) => t.id) }
      : {}),
    ...(o.note ? { note: o.note } : {}),
    ...(o.checkDuplicate ? { checkDuplicate: true } : {}),
  };
}

/** The visit jobs.book found already booked for the customer (QA-698): what to say and open. */
export interface AlreadyBooked {
  message: string;
  jobId: string;
  jobNumber: string;
}
/** A jobs.book refusal that is the already-booked warning, or null for any other refusal. */
export function alreadyBooked(e: { code?: string; message: string; detail?: unknown }): AlreadyBooked | null {
  const d = e.detail as { reason?: string; jobId?: string; jobNumber?: string } | null | undefined;
  return e.code === 'conflict' && d?.reason === 'already_booked' && d.jobId && d.jobNumber
    ? { message: e.message, jobId: d.jobId, jobNumber: d.jobNumber }
    : null;
}

/** The same booking as the work an estimate sold (estimates.bookSold): its customer and address. */
export function bookSoldInput(d: Draft, timeZone: string, estimateId: string, note?: string | null) {
  // its tags aren't asked for: Book doesn't offer them on an estimate's work (bookSold takes none)
  const { appointment, jobTypeId, summary } = bookInput(d, timeZone);
  return {
    estimateId,
    appointment,
    ...(d.businessUnitId ? { businessUnitId: d.businessUnitId } : {}),
    jobTypeId,
    summary,
    ...(note ? { note } : {}),
  };
}

/** The confirmation text, as the customer reads it: from the company's own number, in its name. */
export function confirmationText(p: {
  firstName: string;
  company?: string | null;
  dayLabel: string;
  window: readonly [number, number];
}): string {
  const day = p.dayLabel === 'Today' || p.dayLabel === 'Tomorrow' ? p.dayLabel.toLowerCase() : p.dayLabel;
  const hi = p.company ? `Hi ${p.firstName}, it’s ${p.company}:` : `Hi ${p.firstName},`;
  return sentences(
    `${hi} you’re booked for ${day}, arriving ${windowLabel(p.window)}`,
    'We’ll text when your technician is on the way',
  );
}

export const today = (timeZone: string, at = new Date()): string =>
  new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).format(
    at,
  );

export const hourNow = (timeZone: string, at = new Date()): number =>
  Number(new Intl.DateTimeFormat('en-US', { timeZone, hour: '2-digit', hourCycle: 'h23' }).format(at)) % 24;

/**
 * The address a booking opens on (calls item 5): the call's, when it's one of the customer's, else
 * their first; none when they have none.
 */
export function startLocation(locations: readonly { id: string }[], prefer?: string | null): string | null {
  return locations.find((l) => l.id === prefer)?.id ?? locations[0]?.id ?? null;
}

/** The job's summary from the call's notes (calls item 5), as jobs.book takes it; null with none. */
export function summaryFromNotes(notes?: string | null): string | null {
  const t = notes?.trim();
  return t ? t.slice(0, 4000) : null;
}

/** What the call started on the booking, in words: "Its address and notes start the booking below." */
export function fromCallWords(address: boolean, notes: boolean): string | null {
  if (address && notes) return 'Its address and notes start the booking below.';
  if (address) return 'Its address starts the booking below.';
  if (notes) return 'Its notes start the booking below.';
  return null;
}

/** One arrival window's capacity, as capacity.availability answers it (BL-057). */
export interface WindowCap {
  label: string;
  openMinutes: number;
  freeTechIds: string[];
  fits: boolean | null;
  /** Phase 2; absent from a server before it. */
  totalMinutes?: number;
  bookedMinutes?: number;
  unassignedMinutes?: number;
  isOverIdeal?: boolean;
  adjustments?: { id: string; closed: boolean; minutes: number; reason: string }[];
  /** Against the company's working hours that day (QA-872); absent from a server before it. */
  hours?: 'in' | 'past_close' | 'after_hours';
}

/** Minutes as the chips say them: "2 h", "1 h 30 min", "45 min". */
export function hoursWords(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return [h ? `${h} h` : '', m ? `${m} min` : ''].filter(Boolean).join(' ');
}

/** A window chip's capacity: "2 h open", or "Full" with nothing left to book. */
export const capWords = (w: Pick<WindowCap, 'openMinutes'>): string =>
  w.openMinutes > 0 ? `${hoursWords(w.openMinutes)} open` : 'Full';

/**
 * Why the chosen window is past its capacity, or null when it has room (BL-057): closed by the
 * office, full, nobody who can fit the job type's length starting in it, or past the ideal booking %.
 */
export function capReason(
  w: WindowCap,
  jobType: { durationMinutes: number } | null,
  idealPercent?: number,
): string | null {
  const closed = w.adjustments?.find((a) => a.closed);
  if (closed) return `${w.label} is closed: ${closed.reason}.`.replace(/\.\.$/, '.');
  if (w.openMinutes <= 0) return `${w.label} is full: no technician time is left in it.`;
  if (jobType && w.fits === false)
    return `${w.label} has ${hoursWords(w.openMinutes)} open, but nobody can fit a ${hoursWords(jobType.durationMinutes)} visit starting in it.`;
  if (w.isOverIdeal && idealPercent !== undefined && w.totalMinutes)
    return `${w.label} is past the ideal ${idealPercent} %: ${hoursWords((w.bookedMinutes ?? 0) + (w.unassignedMinutes ?? 0))} of ${hoursWords(w.totalMinutes)} booked.`;
  return null;
}

/** The chosen window's warning, or null: booking goes ahead either way, and the dispatcher sees it. */
export function capWarning(
  w: WindowCap,
  jobType: { durationMinutes: number } | null,
  idealPercent?: number,
): string | null {
  const why = capReason(w, jobType, idealPercent);
  return why && `${why} You can still book; the dispatcher will see it.`;
}

/** The note a booking against capacity leaves on its job (phase 2), as ServiceTitan logs a skipped check. */
export const capNote = (why: string): string => `Booked against capacity: ${why}`;

// ── BL-133: the week of arrival windows (calendar B) ──
// Book shows a week at a time, the arrival windows down and the days across, from one
// capacity.availability read per week (the read covers at most 14 days, from any day). It looks as far
// ahead as booking goes, nine weeks, a week a page; today's page starts today, so no past days show.

/** Days on one page of the week grid: one capacity.availability read each. */
export const WEEK_DAYS = 7;
/** How far ahead Book looks: nine weeks of pages, today on the first. */
export const WEEK_PAGES = 9;

/** The last day booking offers: the last day of the last page. */
export const lastBookDay = (today: string): string => addDays(today, WEEK_DAYS * WEEK_PAGES - 1);

/** The page a day is on (0 is the week from today); a day before today or past the last is on none. */
export function weekPageOf(today: string, date: string): number | null {
  const n = daysBetween(today, date);
  return n < 0 || n >= WEEK_DAYS * WEEK_PAGES ? null : Math.floor(n / WEEK_DAYS);
}

/** One page's days, and the capacity.availability input that reads them: from its first to its last. */
export function weekPage(today: string, page: number): { from: string; to: string; dates: string[] } {
  const p = Math.min(Math.max(0, Math.trunc(page)), WEEK_PAGES - 1);
  const dates = Array.from({ length: WEEK_DAYS }, (_, i) => addDays(today, p * WEEK_DAYS + i));
  return { from: dates[0]!, to: dates.at(-1)!, dates };
}

/** "Today", "Tomorrow", else "Thu, Oct 29": the booked line and the confirmation say the day this way. */
export const dayLabel = (date: string, today: string): string =>
  date === today
    ? 'Today'
    : date === addDays(today, 1)
      ? 'Tomorrow'
      : dateText(date, 'UTC', { weekday: 'short', year: false, now: today });

/** A page's span in words: "Oct 27 to Nov 2". */
export const weekRangeWords = (from: string, to: string): string =>
  `${dateText(from, 'UTC', { year: false, now: from })} to ${dateText(to, 'UTC', { year: false, now: from })}`;

const WEEKDAY = new Intl.DateTimeFormat('en-CA', { weekday: 'short', timeZone: 'UTC' });

/** A day's column heading: its weekday ("Today" for today) and the day of the month. */
export function dayHead(date: string, today: string): { day: string; short: string; num: string } {
  const short = WEEKDAY.format(new Date(`${date}T12:00:00Z`)).replace('.', '');
  return { day: date === today ? 'Today' : short, short, num: String(Number(date.slice(8, 10))) };
}

/** One page's answer from capacity.availability: each day's windows, with their start instants. */
export interface WeekRead {
  jobType: { durationMinutes: number } | null;
  idealPercent?: number;
  days: {
    date: string;
    /** The company's working hours that day, null when it's closed (QA-872). */
    hours?: { open: string; close: string } | null;
    windows: (WindowCap & { start: string })[];
  }[];
}

/** "17:00" → "5 p.m.", "07:30" → "7:30 a.m.": a working-hours time as the grid says it. */
function clockWords(hm: string): string {
  const [h = 0, m = 0] = hm.split(':').map(Number);
  const part = h < 12 ? 'a.m.' : 'p.m.';
  const h12 = h % 12 || 12;
  return `${h12}${m ? `:${String(m).padStart(2, '0')}` : ''} ${part}`;
}

/**
 * The windows a page's grid draws: the standard ones, after the earlier one the server adds for a
 * company that opens before them (QA-872), found by its start in the read.
 */
export function gridWindows(read: WeekRead | null, timeZone: string): (readonly [number, number])[] {
  const first = DAY_WINDOWS[0]![0];
  for (let h = 0; h < first; h++)
    if (read?.days.some((x) => windowCapOn(read, x.date, [h, first], timeZone)))
      return [[h, first], ...DAY_WINDOWS];
  return [...DAY_WINDOWS];
}

/** A daytime window's capacity on a day, from a page's answer, matched by its start (the zone's hour). */
export function windowCapOn(
  read: WeekRead | null | undefined,
  date: string,
  w: readonly [number, number],
  timeZone: string,
): (WindowCap & { start: string }) | undefined {
  const at = Date.parse(zonedInstant(date, w[0], timeZone));
  return read?.days.find((x) => x.date === date)?.windows.find((c) => Date.parse(c.start) === at);
}

/**
 * A cell's state, as the grid draws it: past (already started), closed by the office, full, room but
 * nobody can fit the visit's length starting in it (short), open, or unknown when the read failed.
 */
export type CellState = 'past' | 'closed' | 'full' | 'short' | 'open' | 'unknown';

export interface WeekCell {
  date: string;
  window: readonly [number, number];
  state: CellState;
  openMinutes: number;
  /** The office's reason, on a closed window. */
  closedWhy: string | null;
  /** Today's window that has started and isn't over: still booked into, never the soonest (QA-829). */
  underWay: boolean;
  /** What the cell shows: "2 h", "1½ h", "45 min", "Full", "Closed"; blank once started. */
  text: string;
  /** Its name, said in full: "Thu, Oct 29, 8–10 a.m.: 2 h open, fits 1 h 30 min". */
  name: string;
}

export interface WeekGrid {
  from: string;
  to: string;
  days: { date: string; day: string; short: string; num: string; today: boolean }[];
  rows: { window: readonly [number, number]; label: string; cells: WeekCell[] }[];
}

/** Open time as a narrow cell says it: "2 h", "1½ h", under an hour in minutes. */
export function cellHours(minutes: number): string {
  if (minutes < 60) return `${minutes} min`;
  const halves = Math.round(minutes / 30);
  return `${Math.floor(halves / 2)}${halves % 2 ? '½' : ''} h`;
}

function cellOf(
  date: string,
  w: readonly [number, number],
  c: WindowCap | undefined,
  read: boolean,
  o: {
    today: string;
    nowHour: number;
    length: number | null;
    hours?: { open: string; close: string } | null;
  },
): WeekCell {
  const at = `${dayLabel(date, o.today)}, ${windowLabel(w)}`;
  const underWay = windowStarted(date, o.today, w, o.nowHour) && !windowOver(date, o.today, w, o.nowHour);
  const base = { date, window: w, closedWhy: null, openMinutes: c?.openMinutes ?? 0, underWay };
  if (daysBetween(o.today, date) < 0 || windowOver(date, o.today, w, o.nowHour))
    return { ...base, openMinutes: 0, state: 'past', text: '', name: `${at}: already over` };
  // the window under way is still booked into, said so (QA-829)
  const when = underWay ? `${at}, under way` : at;
  if (!c)
    return read
      ? { ...base, state: 'unknown', text: '–', name: `${when}: open time not known` }
      : { ...base, state: 'unknown', text: '–', name: `${when}: couldn’t get its open time` };
  // Outside the company's working hours: closed that day, or wholly before the open or after the
  // close (QA-872). Still pickable, as a closed window is: Book marks it after hours.
  if (c.hours === 'after_hours') {
    const why = o.hours ? 'outside your working hours' : 'you’re closed that day';
    return { ...base, state: 'closed', closedWhy: why, text: 'Closed', name: `${when}: closed, ${why}` };
  }
  const late =
    c.hours === 'past_close' && o.hours ? `, runs past the ${clockWords(o.hours.close)} close` : '';
  const closed = c.adjustments?.find((a) => a.closed);
  if (closed)
    return {
      ...base,
      state: 'closed',
      closedWhy: closed.reason,
      text: 'Closed',
      name: `${when}: closed, ${closed.reason}`,
    };
  if (c.openMinutes <= 0) return { ...base, state: 'full', text: 'Full', name: `${when}: full${late}` };
  const open = `${hoursWords(c.openMinutes)} open`;
  if (o.length !== null && c.fits === false)
    return {
      ...base,
      state: 'short',
      text: cellHours(c.openMinutes),
      name: `${when}: ${open}, but nobody can fit ${hoursWords(o.length)}${late}`,
    };
  return {
    ...base,
    state: 'open',
    text: cellHours(c.openMinutes),
    name: `${when}: ${open}${o.length !== null && c.fits ? `, fits ${hoursWords(o.length)}` : ''}${late}`,
  };
}

/**
 * One page of the grid: the arrival windows down, the page's days across, each cell's state from the
 * page's read. Without a read (it failed), every window not yet started is unknown, and still pickable.
 */
export function weekGrid(
  today: string,
  page: number,
  read: WeekRead | null,
  o: { nowHour: number; timeZone: string },
): WeekGrid {
  const { from, to, dates } = weekPage(today, page);
  const length = read?.jobType?.durationMinutes ?? null;
  return {
    from,
    to,
    days: dates.map((date) => ({ date, ...dayHead(date, today), today: date === today })),
    rows: gridWindows(read, o.timeZone).map((w) => ({
      window: w,
      label: windowLabel(w),
      cells: dates.map((date) =>
        cellOf(date, w, read ? windowCapOn(read, date, w, o.timeZone) : undefined, !!read, {
          today,
          nowHour: o.nowHour,
          length,
          hours: read?.days.find((x) => x.date === date)?.hours,
        }),
      ),
    })),
  };
}

/**
 * A cell the visit fits: open, and (with a job type) someone can take its length starting in it. The
 * window under way is offered, never put forward as the soonest (QA-829).
 */
export const fitsIn = (c: WeekCell): boolean => c.state === 'open' && !c.underWay;

/**
 * The soonest window that fits, over the pages in order, day by day and each day's windows in order:
 * found; or the page still to read before it's known; or none in the nine weeks; or unknown, when a
 * page's read failed before anything fitting.
 */
export function soonestFit(
  pages: readonly (WeekGrid | null | undefined)[],
): { found: WeekCell; page: number } | { need: number } | { none: true } | { unknown: true } {
  for (let p = 0; p < WEEK_PAGES; p++) {
    const g = pages[p];
    if (g === undefined) return { need: p };
    if (g === null) return { unknown: true };
    for (let day = 0; day < g.days.length; day++)
      for (const r of g.rows) {
        const c = r.cells[day]!;
        if (fitsIn(c)) return { found: c, page: p };
      }
  }
  return { none: true };
}

/** Arrow keys over the grid: left and right a day, up and down a window, Home and End the row's ends. */
export function moveCell(
  at: { row: number; col: number },
  key: string,
  size: { rows: number; cols: number },
): { row: number; col: number } | null {
  const clamp = (n: number, max: number) => Math.min(Math.max(0, n), max - 1);
  switch (key) {
    case 'ArrowLeft':
      return { row: at.row, col: clamp(at.col - 1, size.cols) };
    case 'ArrowRight':
      return { row: at.row, col: clamp(at.col + 1, size.cols) };
    case 'ArrowUp':
      return { row: clamp(at.row - 1, size.rows), col: at.col };
    case 'ArrowDown':
      return { row: clamp(at.row + 1, size.rows), col: at.col };
    case 'Home':
      return { row: at.row, col: 0 };
    case 'End':
      return { row: at.row, col: size.cols - 1 };
    default:
      return null;
  }
}
